import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, X } from 'lucide-react';

const AirbnbBookingWidget = ({ property }) => {
  const airbnbListingUrl = "https://www.airbnb.co.uk/rooms/10280192";
  const MIN_NIGHTS = 3; // Minimum 3-day stay
  const MAX_GUESTS = 4; // Maximum guests (not including infants)
  
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [pets, setPets] = useState(0);
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const [showCalendar, setShowCalendar] = useState(null); // 'checkin' or 'checkout'
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [nights, setNights] = useState(0);
  const [total, setTotal] = useState(0);
  const [minNightsError, setMinNightsError] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState(new Set());
  const [calendarLoading, setCalendarLoading] = useState(true);
  
  const calendarRef = useRef(null);
  
  // Total guests (not including infants)
  const totalGuests = adults + children;

  const NIGHTLY_RATE = 136.43;
  
  // Fetch unavailable dates from Airbnb calendar
  useEffect(() => {
    const fetchUnavailableDates = async () => {
      try {
        // Detect environment: use /api/calendar on Vercel, backend API on preview
        const isVercel = typeof window !== 'undefined' && 
          !window.location.hostname.includes('preview.emergentagent.com');
        
        const apiUrl = isVercel 
          ? '/api/calendar'
          : `${process.env.REACT_APP_BACKEND_URL}/api/calendar/unavailable`;
        
        const response = await fetch(apiUrl);
        const data = await response.json();
        if (data.success && data.unavailable_dates) {
          setUnavailableDates(new Set(data.unavailable_dates));
        }
      } catch (error) {
        console.error('Failed to fetch calendar availability:', error);
      } finally {
        setCalendarLoading(false);
      }
    };
    
    fetchUnavailableDates();
  }, []);
  
  // Tiered pricing based on number of nights
  const getNightlyRate = (numNights) => {
    if (numNights >= 5) return 128.64;
    if (numNights >= 4) return 131.56;
    return 136.43; // 3 nights
  };
  
  // Calculate nights and total
  useEffect(() => {
    if (checkIn && checkOut) {
      const diffTime = checkOut - checkIn;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 0) {
        setNights(diffDays);
        setMinNightsError(diffDays < MIN_NIGHTS);
        const rate = getNightlyRate(diffDays);
        const subtotal = diffDays * rate;
        setTotal(subtotal);
      }
    } else {
      setNights(0);
      setTotal(0);
      setMinNightsError(false);
    }
  }, [checkIn, checkOut]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleReserve = () => {
    const params = new URLSearchParams();
    
    if (checkIn) params.append('check_in', checkIn.toISOString().split('T')[0]);
    if (checkOut) params.append('check_out', checkOut.toISOString().split('T')[0]);
    params.append('adults', adults.toString());
    if (children > 0) params.append('children', children.toString());
    if (infants > 0) params.append('infants', infants.toString());
    
    const url = airbnbListingUrl + '?' + params.toString();
    if (window.va) window.va('event', { name: 'airbnb_click', data: { location: 'booking_widget_reserve' } });
    window.open(url, '_blank');
  };

  const formatDisplayDate = (date) => {
    if (!date) return 'Add date';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  // Check if a date range has any unavailable dates
  const hasBlockedDatesInRange = (startDate, endDate) => {
    let current = new Date(startDate);
    while (current < endDate) {
      const dateStr = current.toISOString().split('T')[0];
      if (unavailableDates.has(dateStr)) return true;
      current.setDate(current.getDate() + 1);
    }
    return false;
  };

  // Find the next available checkout date after check-in (accounting for min nights and blocked dates)
  const findValidCheckout = (checkinDate) => {
    const minCheckout = new Date(checkinDate);
    minCheckout.setDate(minCheckout.getDate() + MIN_NIGHTS);
    
    // Check if the minimum stay period has any blocked dates
    if (hasBlockedDatesInRange(checkinDate, minCheckout)) {
      return null; // No valid checkout possible
    }
    return minCheckout;
  };

  const handleDateSelect = (date) => {
    if (showCalendar === 'checkin') {
      setCheckIn(date);
      // Try to auto-set checkout to minimum 3 nights later, but only if valid
      const validCheckout = findValidCheckout(date);
      if (validCheckout && (!checkOut || checkOut <= validCheckout || hasBlockedDatesInRange(date, checkOut))) {
        setCheckOut(validCheckout);
      } else if (!validCheckout) {
        // Clear checkout if the auto-checkout would be blocked
        setCheckOut(null);
      }
      setShowCalendar('checkout');
    } else {
      setCheckOut(date);
      setShowCalendar(null);
    }
  };

  // Calendar component
  const CalendarPopup = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Earliest check-in is tomorrow
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const getDaysInMonth = (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const daysInMonth = lastDay.getDate();
      const startingDay = firstDay.getDay();
      
      const days = [];
      for (let i = 0; i < startingDay; i++) {
        days.push(null);
      }
      for (let i = 1; i <= daysInMonth; i++) {
        days.push(new Date(year, month, i));
      }
      return days;
    };

    const days = getDaysInMonth(calendarMonth);
    const monthName = calendarMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

    const isDisabled = (date) => {
      if (!date) return true;
      // Check-in must be tomorrow or later
      if (date < tomorrow) return true;
      
      // Check if date is unavailable (booked on Airbnb)
      const dateStr = date.toISOString().split('T')[0];
      if (unavailableDates.has(dateStr)) return true;
      
      if (showCalendar === 'checkin') {
        // For check-in: also disable if there aren't MIN_NIGHTS consecutive available days
        const minCheckout = new Date(date);
        minCheckout.setDate(minCheckout.getDate() + MIN_NIGHTS);
        
        let checkDate = new Date(date);
        while (checkDate < minCheckout) {
          const checkStr = checkDate.toISOString().split('T')[0];
          if (unavailableDates.has(checkStr)) return true;
          checkDate.setDate(checkDate.getDate() + 1);
        }
      }
      
      if (showCalendar === 'checkout' && checkIn) {
        // Must be at least MIN_NIGHTS after check-in
        const minCheckout = new Date(checkIn);
        minCheckout.setDate(minCheckout.getDate() + MIN_NIGHTS);
        if (date < minCheckout) return true;
        
        // Check if any date in the range is unavailable
        let checkDate = new Date(checkIn);
        while (checkDate < date) {
          const checkStr = checkDate.toISOString().split('T')[0];
          if (unavailableDates.has(checkStr)) return true;
          checkDate.setDate(checkDate.getDate() + 1);
        }
      }
      return false;
    };
    
    const isUnavailable = (date) => {
      if (!date) return false;
      const dateStr = date.toISOString().split('T')[0];
      return unavailableDates.has(dateStr);
    };

    const isSelected = (date) => {
      if (!date) return false;
      if (checkIn && date.getTime() === checkIn.getTime()) return true;
      if (checkOut && date.getTime() === checkOut.getTime()) return true;
      return false;
    };

    const isInRange = (date) => {
      if (!date || !checkIn || !checkOut) return false;
      return date > checkIn && date < checkOut;
    };

    return (
      <div 
        ref={calendarRef}
        className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-200 p-5 z-50"
      >
        {calendarLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-gray-900"></div>
            <span className="ml-2 text-sm text-gray-500">Loading availability...</span>
          </div>
        ) : (
        <>
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="font-semibold text-gray-900">{monthName}</span>
          <button 
            onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {days.map((date, i) => (
            <button
              key={i}
              onClick={() => date && !isDisabled(date) && handleDateSelect(date)}
              disabled={isDisabled(date)}
              className={`
                aspect-square flex items-center justify-center text-sm rounded-full transition-all relative
                ${!date ? '' : ''}
                ${isDisabled(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-900'}
                ${isSelected(date) ? 'bg-gray-900 text-white hover:bg-gray-800' : ''}
                ${isInRange(date) ? 'bg-gray-100' : ''}
                ${isUnavailable(date) ? 'line-through decoration-gray-400' : ''}
              `}
            >
              {date?.getDate()}
            </button>
          ))}
        </div>
        
        <button 
          onClick={() => setShowCalendar(null)}
          className="mt-4 w-full text-center text-sm font-medium text-gray-500 hover:text-gray-900"
        >
          Close
        </button>
        
        {/* Sync indicator */}
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-center gap-1.5">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span className="text-[10px] text-gray-400">Synced with Airbnb</span>
        </div>
        </>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl md:rounded-2xl shadow-xl border border-gray-100 p-4 md:p-6 w-full max-w-sm">
      {/* Price header - Show "Add dates for prices" until dates selected */}
      <div className="mb-4 md:mb-6">
        {nights > 0 ? (
          <div className="flex items-baseline gap-1">
            <span className="text-2xl md:text-3xl font-semibold text-gray-900">£{getNightlyRate(nights).toFixed(2)}</span>
            <span className="text-gray-500 text-sm md:text-base">night</span>
          </div>
        ) : (
          <p className="text-base md:text-lg font-medium text-gray-700">Add dates for prices</p>
        )}
      </div>

      {/* Date and Guest selector */}
      <div className="relative border border-gray-300 rounded-lg md:rounded-xl overflow-visible mb-3 md:mb-4">
        {/* Date row */}
        <div className="grid grid-cols-2">
          <button 
            onClick={() => setShowCalendar(showCalendar === 'checkin' ? null : 'checkin')}
            className="p-3 md:p-4 text-left border-r border-b border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <div className="text-[9px] md:text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-0.5 md:mb-1">
              CHECK-IN
            </div>
            <div className={`text-xs md:text-sm ${checkIn ? 'text-gray-900' : 'text-gray-400'}`}>
              {formatDisplayDate(checkIn)}
            </div>
          </button>
          <button 
            onClick={() => setShowCalendar(showCalendar === 'checkout' ? null : 'checkout')}
            className="p-3 md:p-4 text-left border-b border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <div className="text-[9px] md:text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-0.5 md:mb-1">
              CHECKOUT
            </div>
            <div className={`text-xs md:text-sm ${checkOut ? 'text-gray-900' : 'text-gray-400'}`}>
              {formatDisplayDate(checkOut)}
            </div>
          </button>
        </div>

        {/* Calendar popup */}
        {showCalendar && <CalendarPopup />}

        {/* Guests row */}
        <div className="relative">
          <button
            onClick={() => setShowGuestDropdown(!showGuestDropdown)}
            className="w-full p-3 md:p-4 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
          >
            <div>
              <div className="text-[9px] md:text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-0.5 md:mb-1">
                GUESTS
              </div>
              <div className="text-xs md:text-sm text-gray-900">
                {totalGuests} guest{totalGuests !== 1 ? 's' : ''}{infants > 0 ? `, ${infants} infant${infants !== 1 ? 's' : ''}` : ''}
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 md:w-5 md:h-5 text-gray-400 transition-transform ${showGuestDropdown ? 'rotate-180' : ''}`} />
          </button>

          {/* Guest dropdown */}
          {showGuestDropdown && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg md:rounded-xl shadow-xl z-50 p-3 md:p-4">
              {/* Adults */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <div className="font-medium text-gray-900 text-sm md:text-base">Adults</div>
                  <div className="text-[10px] md:text-xs text-gray-500">Age 13+</div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setAdults(Math.max(1, adults - 1))}
                    className="w-7 h-7 md:w-8 md:h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-900 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                    disabled={adults <= 1}
                  >
                    −
                  </button>
                  <span className="w-5 md:w-6 text-center font-medium text-sm md:text-base">{adults}</span>
                  <button
                    onClick={() => setAdults(adults + 1)}
                    className="w-7 h-7 md:w-8 md:h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-900 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                    disabled={totalGuests >= MAX_GUESTS}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Children */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <div className="font-medium text-gray-900 text-sm md:text-base">Children</div>
                  <div className="text-[10px] md:text-xs text-gray-500">Ages 2–12</div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setChildren(Math.max(0, children - 1))}
                    className="w-7 h-7 md:w-8 md:h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-900 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                    disabled={children <= 0}
                  >
                    −
                  </button>
                  <span className="w-5 md:w-6 text-center font-medium text-sm md:text-base">{children}</span>
                  <button
                    onClick={() => setChildren(children + 1)}
                    className="w-7 h-7 md:w-8 md:h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-900 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                    disabled={totalGuests >= MAX_GUESTS}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Infants */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <div className="font-medium text-gray-900 text-sm md:text-base">Infants</div>
                  <div className="text-[10px] md:text-xs text-gray-500">Under 2</div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setInfants(Math.max(0, infants - 1))}
                    className="w-7 h-7 md:w-8 md:h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-900 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                    disabled={infants <= 0}
                  >
                    −
                  </button>
                  <span className="w-5 md:w-6 text-center font-medium text-sm md:text-base">{infants}</span>
                  <button
                    onClick={() => setInfants(infants + 1)}
                    className="w-7 h-7 md:w-8 md:h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-900 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                    disabled={infants >= 5}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Pets */}
              <div className="flex items-center justify-between py-3">
                <div>
                  <div className="font-medium text-gray-900 text-sm md:text-base">Pets</div>
                  <div className="text-[10px] md:text-xs text-gray-500">
                    <a 
                      href="https://www.airbnb.co.uk/help/article/1869" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="underline hover:text-gray-700"
                      onClick={() => { if (window.va) window.va('event', { name: 'airbnb_click', data: { location: 'service_animal_help' } }); }}
                    >
                      Bringing a service animal?
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    className="w-7 h-7 md:w-8 md:h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-400 opacity-30 cursor-not-allowed text-sm"
                    disabled
                  >
                    −
                  </button>
                  <span className="w-5 md:w-6 text-center font-medium text-sm md:text-base text-gray-400">0</span>
                  <button
                    className="w-7 h-7 md:w-8 md:h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-400 opacity-30 cursor-not-allowed text-sm"
                    disabled
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Info text */}
              <p className="text-[10px] md:text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">
                This place has a maximum of {MAX_GUESTS} guests, not including infants. Pets aren't allowed.
              </p>

              <button
                onClick={() => setShowGuestDropdown(false)}
                className="mt-3 md:mt-4 w-full text-right text-xs md:text-sm font-semibold text-gray-900 underline"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Minimum stay notice */}
      <div className="flex items-center gap-2 mb-3 md:mb-4 text-xs md:text-sm text-gray-600 bg-gray-50 rounded-lg px-2.5 md:px-3 py-1.5 md:py-2">
        <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-400" />
        <span>Minimum stay: {MIN_NIGHTS} nights</span>
      </div>

      {/* Minimum nights error */}
      {minNightsError && (
        <div className="mb-3 md:mb-4 text-xs md:text-sm text-red-600 bg-red-50 rounded-lg px-2.5 md:px-3 py-1.5 md:py-2">
          Please select at least {MIN_NIGHTS} nights for your stay.
        </div>
      )}

      {/* Reserve button */}
      <button
        onClick={handleReserve}
        disabled={minNightsError || !checkIn || !checkOut}
        className="w-full py-3 md:py-4 rounded-lg md:rounded-xl font-semibold text-white text-base md:text-lg flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: 'linear-gradient(to right, #E61E4D, #E31C5F, #D70466)' }}
        data-testid="reserve-btn"
      >
        Reserve
        <ExternalLink className="w-3.5 h-3.5 md:w-4 md:h-4" />
      </button>

      <p className="text-center text-xs md:text-sm text-gray-500 mt-2 md:mt-3">
        You won't be charged yet
      </p>

      {/* Price breakdown */}
      {nights > 0 && (
        <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-200 space-y-2 md:space-y-3">
          <div className="flex justify-between text-gray-600 text-xs md:text-sm">
            <span className="underline decoration-dotted">~£{getNightlyRate(nights).toFixed(0)} × {nights} nights</span>
            <span>~£{Math.round(getNightlyRate(nights) * nights)}</span>
          </div>
          <div className="flex justify-between font-semibold text-gray-900 pt-3 md:pt-4 border-t border-gray-200 text-sm md:text-base">
            <span>Estimated total</span>
            <span>~£{Math.round(total)}</span>
          </div>
          <p className="text-[10px] md:text-xs text-amber-600 text-center bg-amber-50 rounded px-2 py-1.5">
            Estimated price — actual price may vary based on seasonal rates, fees & availability. See final price on Airbnb.
          </p>
        </div>
      )}

      {/* Redirect notice */}
      <p className="mt-4 md:mt-5 text-[10px] md:text-xs text-gray-400 text-center leading-relaxed">
        You'll complete your booking securely on Airbnb
      </p>
      
      {/* Powered by Airbnb */}
      <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gray-100 flex items-center justify-center gap-2">
        <span className="text-[10px] md:text-xs text-gray-400">Powered by</span>
        <img 
          src="https://upload.wikimedia.org/wikipedia/commons/6/69/Airbnb_Logo_B%C3%A9lo.svg" 
          alt="Airbnb" 
          className="h-4 md:h-5 w-auto"
        />
      </div>
    </div>
  );
};

export default AirbnbBookingWidget;
